import { OpusAgentAttachmentPhase } from "@src/types.generated";
import type { OpusAgentAttachmentStatusFieldsFragment } from "./useOpusAgentAttachments.generated";

const extensionOf = (fileName: string): string =>
  fileName.includes(".") ? (fileName.split(".").pop() || "").toLowerCase() : "";

// Files the server pulls pages (or slides) out of; the rest it just reads.
const PAGED_EXTENSIONS = ["pdf", "docx", "pptx"];

// What a composer pill says while its file is on the way: the step the
// upload or the server is on, so a long read shows it is moving.
export const workingCopy = (
  state: "uploading" | "processing",
  uploadPercent: number | null | undefined,
  status: OpusAgentAttachmentStatusFieldsFragment | null | undefined,
  fileName?: string,
): string => {
  if (state === "uploading")
    return uploadPercent != null
      ? `Uploading… ${uploadPercent}%`
      : "Uploading…";
  switch (status?.phase) {
    case OpusAgentAttachmentPhase.Queued:
      return "Waiting to read…";
    case OpusAgentAttachmentPhase.Extracting:
      return PAGED_EXTENSIONS.includes(extensionOf(fileName ?? ""))
        ? "Extracting pages…"
        : "Reading file…";
    case OpusAgentAttachmentPhase.ReadingImages:
      return status.progressDone != null && status.progressTotal != null
        ? `Reading images · ${status.progressDone} of ${status.progressTotal}`
        : "Reading images…";
    case OpusAgentAttachmentPhase.PreparingImages:
      return "Preparing images…";
    case OpusAgentAttachmentPhase.ReadingImage:
      return "Reading text in image…";
    case OpusAgentAttachmentPhase.PreparingImage:
      return "Preparing image…";
    default:
      // Before the server's first answer.
      return "Reading…";
  }
};

// The kind of file, as the author would name it.
const KIND_BY_EXTENSION: Record<string, string> = {
  pdf: "PDF",
  docx: "Word doc",
  pptx: "PowerPoint",
  xlsx: "Spreadsheet",
  xls: "Spreadsheet",
  csv: "CSV",
  txt: "Text",
  html: "Web page",
  htm: "Web page",
  png: "Image",
  jpg: "Image",
  jpeg: "Image",
  webp: "Image",
};

const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

// A read document that can be sent while its pictures are still being read:
// say both.
export const readingImagesCopy = (
  status: OpusAgentAttachmentStatusFieldsFragment,
): string =>
  status.progressDone != null && status.progressTotal != null
    ? `Reading images · ${status.progressDone} of ${status.progressTotal} · you can send`
    : "Reading images · you can send";

// What a read upload says once it is ready: its kind plus what the read
// found, e.g. "PDF · 2 pages" or "Image · text found". Null when the kind is
// unknown, so the caller keeps its own label.
export const readyCopy = (
  fileName: string,
  status: OpusAgentAttachmentStatusFieldsFragment | null | undefined,
): string | null => {
  if (status?.phase === OpusAgentAttachmentPhase.ReadingImages)
    return readingImagesCopy(status);
  const extension = extensionOf(fileName);
  const kind = KIND_BY_EXTENSION[extension];
  if (!kind) return null;
  const pageCount = status?.pageCount;
  if (extension === "pdf" && pageCount != null)
    return `${kind} · ${plural(pageCount, "page", "pages")}`;
  if (extension === "pptx" && pageCount != null)
    return `${kind} · ${plural(pageCount, "slide", "slides")}`;
  if (kind === "Image" && status?.textFound) return `${kind} · text found`;
  return kind;
};
